import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

export const AddMemberModal = ({ groupId, onClose, fetchGroups, groupMembers = [], refreshGroupDetails }) => {
    const [contacts, setContacts] = useState([]);
    // track selected member ids to avoid object-equality issues
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true); // for initial contacts load

    const token = Cookies.get("auth_token");

    useEffect(() => {
        fetchContacts();
    }, []);

    // 🔥 Get all contacts
    const fetchContacts = async () => {
        setFetching(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-contacts`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            const data = await res.json();
            const all = data?.data?.[0]?.contacts || [];
            // Only include contacts that are PhoneBook users
            const filtered = all.filter((c) => Number(c.phone_book_user) === 1);
            setContacts(filtered);
        } catch (err) {
            } finally {
            setFetching(false);
        }
    };

    // Toggle select using a stable id: prefer user_id, fall back to phone or phone_number
    const getId = (person) => person.user_id || person.phone || person.phone_number;
    const toggleSelect = (person) => {
        const id = getId(person);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // 🔥 Confirm → Call API to add members (comma-separated ids and phones)
    const handleConfirm = async () => {
        // derive selected members from contacts using selectedIds
        const selectedMembers = contacts.filter(c => selectedIds.has(getId(c)));
        if (selectedMembers.length === 0) return;

        setLoading(true);

        try {
            const userIds = selectedMembers.map(s => s.user_id).join(",");
            const phones = selectedMembers.map(s => s.phone_number).join(",");

            const body = new URLSearchParams();
            body.append("group_id", groupId);
            body.append("member_user_ids", userIds); // comma separated
            body.append("member_phones", phones); // comma separated

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/group/add_members`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: body.toString(),
                }
            );

            const data = await res.json();

            if (data.status === "1") {
                toast.success("Members added!");
                // Refresh group list and group details if available
                try {
                    fetchGroups && fetchGroups();
                } catch (e) {
                    }
                try {
                    refreshGroupDetails && refreshGroupDetails(groupId);
                } catch (e) {
                    }
                onClose();
            } else {
                toast.dismiss();
                toast.error(data.message || "Failed to add members");
            }
        } catch (err) {
            toast.dismiss();
            toast.error("Failed to add members");
        }

        setLoading(false);
    };

    const memberIdSet = new Set(
        (groupMembers || []).map((m) => m.user_id || m.phone || m.phone_number).filter(Boolean)
    );

    const filteredContacts = contacts
        // Exclude those already in the group
        .filter((c) => !memberIdSet.has(getId(c)))
        // Apply search filter
        .filter((c) => c.full_name?.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999]">
            <div className="bg-white rounded-3xl shadow-2xl w-[420px] max-h-[85vh] overflow-hidden relative flex flex-col">

                {/* Sticky Header + Search */}
                <div className="sticky top-0 bg-white pb-3 z-20 pt-6 px-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Add Member</h2>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="p-2 rounded-full hover:bg-gray-100"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 8.586L15.95 2.636a1 1 0 111.414 1.414L11.414 10l5.95 5.95a1 1 0 01-1.414 1.414L10 11.414l-5.95 5.95A1 1 0 012.636 15.95L8.586 10 2.636 4.05A1 1 0 014.05 2.636L10 8.586z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="relative w-full mb-3">
                         <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10">
                        <img
                            src="../chatbox/magnifer.svg"
                            alt="magnifer"
                            className=" w-5 h-5"
                        />
                    </div>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search"
                        className="w-full rounded-[20px] border border-gray-400 bg-white pl-12 pr-12 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-md shadow-[#7C4DFF] sticky top-4 [appearance:textfield] [&::-webkit-search-cancel-button]:hidden"
                        />
                    </div>
                </div>

                {/* Contacts list (fixed height, scrollable) */}
                <div className="space-y-3 pb-4 px-6 flex-1 overflow-y-auto max-h-[40vh]    [&::-webkit-scrollbar]:w-1
            [&::-webkit-scrollbar-thumb]:bg-gray-400 
            [&::-webkit-scrollbar-thumb]:rounded-full 
            [&::-webkit-scrollbar-track]:bg-gray-100">
                    {fetching ? (
                        // Loading skeleton similar to viewcolleaguemodal
                        <>
                            {Array.from({ length: 5 }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className="flex items-center justify-between p-3 rounded-[100px] bg-gray-200 animate-pulse"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-gray-300" />
                                        <div className="flex-1">
                                            <div className="h-4 w-24 bg-gray-300 rounded mb-2" />
                                            <div className="h-3 w-20 bg-gray-300 rounded" />
                                        </div>
                                    </div>
                                    <div className="h-6 w-6 rounded-full bg-gray-300" />
                                </div>
                            ))}
                        </>
                    ) : filteredContacts.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">
                            {contacts.length === 0 ? (
                                <>
                                    <p className="font-semibold">No contacts available</p>
                                    <p className="text-xs">You don't have any contacts yet.</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold">No results</p>
                                    <p className="text-xs">No contacts match "{search}".</p>
                                </>
                            )}
                        </div>
                    ) : (
                        filteredContacts.map((person) => {
                            const isSelected = selectedIds.has(getId(person));

                            return (
                                <div
                                    key={person.user_id || person.phone || person.phone_number}
                                    onClick={() => toggleSelect(person)}
                                    className={`flex items-center justify-between p-3 rounded-[100px] border shadow-md cursor-pointer ${isSelected
                                        ? "bg-[#EDE7FF] border-[#7C4DFF]"
                                        : "bg-[#797C7B] bg-opacity-[11%]"
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={
                                                person.profile_image
                                                    ? `${process.env.REACT_APP_API_BASE_URL}${person.profile_image}`
                                                    : "/default-avatar.png"
                                            }
                                            className="h-10 w-10 rounded-full object-cover"
                                        />

                                        <div>
                                            <p className="font-semibold text-sm">{person.full_name}</p>
                                            <p className="text-xs text-gray-500">{person.phone || person.phone_number}</p>
                                        </div>
                                    </div>

                                    {/* Radio / checkbox */}
                                    <div
                                        className={`w-6 h-6 flex items-center justify-center rounded-full border-2 ${isSelected ? "bg-[#7C4DFF] border-[#7C4DFF]" : "border-gray-400"
                                            }`}
                                    >
                                        {isSelected && (
                                            <img
                                                src="../chatscreen/radiobutton.svg"
                                                alt="selected"
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                {/* Sticky footer confirm button */}
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 z-20">
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading || selectedIds.size === 0}
                        className={`w-full submit-button ${loading || selectedIds.size === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                        {loading ? "Adding..." : `Add ${selectedIds.size || ""}${selectedIds.size ? " member(s)" : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
};
// ...existing code...